/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import { Button, message } from 'antd';
import { useIntl } from 'react-intl';
import { useMutation, queryCache } from 'react-query';

import { appLog } from '~src/utils/log';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import QuestionForm, {
  defaultQuestionFormValues,
  QuestionFormValues,
} from '~components/atoms/Questions/QuestionForm';
import { addQuestion } from '~src/services/api/questions';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './CreateTenderQuestionForm.styles';

export interface CreateTenderQuestionFormProps {
  shareId: string;
  shareKey?: string;
  onCreateSuccess?: () => void;
  onCancel?: () => void;
}

export const CreateTenderQuestionForm: React.FC<CreateTenderQuestionFormProps> = ({
  shareId,
  shareKey,
  onCreateSuccess,
  onCancel,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const [formValues, setFormValues] = useState<QuestionFormValues>(
    defaultQuestionFormValues
  );

  const shareParams = {
    share_id: shareId,
    share_key: shareKey,
  };

  const [addQuestionMutation, { isLoading }] = useMutation(addQuestion, {
    onSuccess: () => {
      queryCache.invalidateQueries([
        QueryKeys.sharedTenderQuestion,
        shareParams,
      ]);
      onCreateSuccess?.();
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'voucher-assigning-failed' }));
      appLog('addQuestionMutation -> item', item);
      appLog('addQuestionMutation -> err', error);
    },
  });

  const isValid = () => {
    if (!formValues.title) return false;

    return true;
  };

  const handleFormChange = (key: keyof QuestionFormValues, newValue: any) => {
    setFormValues((prevForm) => ({
      ...prevForm,
      [key]: newValue,
    }));
  };

  const handleSubmit = async () => {
    if (!isValid()) return;

    await addQuestionMutation({
      share_id: shareId,
      share_key: shareKey,
      data: formValues,
    });
  };

  return (
    <div css={styles.pageContainer}>
      <div css={styles.formContainer}>
        <QuestionForm
          formValues={formValues}
          handleValueChange={handleFormChange}
        />
        <div css={styles.buttonContainer}>
          <Button onClick={onCancel} css={styles.cancelBtn}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} type="primary">
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};
